import { GridRowSelectionModel } from '@mui/x-data-grid-premium'
import { ChevronDown, Copy, Trash2, X } from 'lucide-react'
import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react'
import { MenuDropdown, type MenuDropdownItem } from '~/@/components/inputs/selection/menu-dropdown'
import { Button } from '~/@/components/ui/button'
import { cn } from '~/@/lib/utils'
import {
  BooleanOperator,
  BulkDeleteTableRowsMutation,
  GetBulkActionEligibilityCountsQuery,
  TableFilterInput,
  useBulkDeleteTableRowsMutation,
  useBulkDuplicateTableRowsMutation,
  useExecuteBulkActionMutation,
  useGetBulkActionEligibilityCountsLazyQuery,
} from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { isButtonDestructive } from '~/common/utils/button'
import { ConfirmationDialog } from '~/components/ConfirmationDialog'
import { DossIcon } from '~/components/DossIcon'
import {
  BlockingReferencesDisplay,
  type BlockingReferenceDisplay,
} from '~/components/shared/BlockingReferencesDisplay'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import { DetailedTable, DetailedTableRow } from '~/components/tables/types'
import { BulkActionPreviewDialog } from './BulkActionPreviewDialog'
import { useRowProcessingState } from './useRowProcessingState'

type ButtonEligibilityCount =
  GetBulkActionEligibilityCountsQuery['bulkActionEligibilityCounts']['buttonEligibilityCounts'][number]

export interface BulkActionsMenuProps {
  table: DetailedTable
  tableId: string
  rowSelectionModel: GridRowSelectionModel
  isSelectAll: boolean
  totalRowCount: number
  currentFilters?: TableFilterInput[]
  currentFilterBooleanOperator: BooleanOperator
  tableTabId?: string
  hasUnsavedFilters: boolean
  searchString?: string
  onClearSelection?: () => void
  setRowsData: Dispatch<SetStateAction<DetailedTableRow[]>>
}

const MAX_VISIBLE_BUTTONS = 3
const MAX_VISIBLE_DELETE_ERROR_NAMES = 3

type BulkDeleteError = NonNullable<
  BulkDeleteTableRowsMutation['bulkDeleteTableRows']['errors']
>[number]
type BulkActionErrorMessage = {
  rowId: string
  rowName?: string | null
  message: string
}
type TypedTFunction = ReturnType<typeof useTypedTranslation>['t']

/**
 * Converts bulk delete errors to BlockingReferenceDisplay format for the shared component.
 */
const convertErrorsToBlockingReferences = (
  errors: BulkDeleteError[],
  legacyTableName: string,
): BlockingReferenceDisplay[] => {
  const references: BlockingReferenceDisplay[] = []

  for (const error of errors) {
    const blockedRowName = error.rowName ?? error.rowId

    // Prefer blockingReferences (has context) over blockingRows (legacy)
    if (error.blockingReferences && error.blockingReferences.length > 0) {
      for (const ref of error.blockingReferences) {
        references.push({
          referencingRowId: ref.row.id,
          referencingRowName: ref.row.name,
          blockedRowName,
          columnName: ref.columnName,
          tableName: ref.tableName,
          tableId: ref.tableId,
        })
      }
    } else if (error.blockingRows && error.blockingRows.length > 0) {
      // Legacy: no table info, use a placeholder
      for (const blockingRow of error.blockingRows) {
        references.push({
          referencingRowId: blockingRow.id,
          referencingRowName: blockingRow.name,
          blockedRowName,
          columnName: '',
          tableName: legacyTableName,
          tableId: '_legacy_',
        })
      }
    }
  }

  return references
}

const formatRowNameList = (rowNames: string[], useFinalConjunction = true) => {
  if (rowNames.length === 0) {
    return ''
  }

  if (rowNames.length === 1) {
    return rowNames[0]
  }

  if (rowNames.length === 2 && useFinalConjunction) {
    return `${rowNames[0]} and ${rowNames[1]}`
  }

  if (!useFinalConjunction) {
    return rowNames.join(', ')
  }

  return `${rowNames.slice(0, -1).join(', ')}, and ${rowNames.at(-1)}`
}

const getBulkDeleteErrorMessage = (errors: BulkActionErrorMessage[], t: TypedTFunction) => {
  if (errors.length === 0) {
    return null
  }

  const uniqueRowNames = Array.from(
    new Set(errors.map((error) => error.rowName).filter((name): name is string => !!name)),
  )

  if (errors.length === 1) {
    const rowName = uniqueRowNames[0]
    return rowName
      ? t('table.bulk_actions.delete_error_single_named', { rowName })
      : t('table.bulk_actions.delete_error_single_unnamed')
  }

  if (uniqueRowNames.length === 0) {
    return t('table.bulk_actions.delete_error_multiple_unnamed', {
      count: errors.length,
    })
  }

  const visibleRowNames = uniqueRowNames.slice(0, MAX_VISIBLE_DELETE_ERROR_NAMES)
  const remainingCount = Math.max(errors.length - visibleRowNames.length, 0)
  const rowNames = formatRowNameList(visibleRowNames)

  if (remainingCount === 0) {
    return t('table.bulk_actions.delete_error_multiple_named', { rowNames })
  }

  return t('table.bulk_actions.delete_error_multiple_named_with_others', {
    count: remainingCount,
    rowNames: formatRowNameList(visibleRowNames, false),
  })
}

export function BulkActionsMenu({
  table,
  tableId,
  rowSelectionModel,
  isSelectAll,
  totalRowCount,
  currentFilters,
  currentFilterBooleanOperator,
  tableTabId,
  hasUnsavedFilters,
  searchString,
  onClearSelection,
  setRowsData,
}: BulkActionsMenuProps) {
  const { t } = useTypedTranslation()
  const snackbar = useDossSnackbar()
  const { permissionsManager } = useGlobalContext()
  const selectedCount = rowSelectionModel.length
  const isMultiSelect = selectedCount > 1

  type ConfirmationType = 'delete' | 'duplicate' | 'button'
  const [confirmationState, setConfirmationState] = useState<{
    open: boolean
    type: ConfirmationType | null
    button: ButtonEligibilityCount | null
  }>({ open: false, type: null, button: null })

  const [previewState, setPreviewState] = useState<{
    open: boolean
    button: ButtonEligibilityCount | null
  }>({ open: false, button: null })

  const [isHovering, setIsHovering] = useState(false)

  const [getEligibilityCounts, { data, previousData }] = useGetBulkActionEligibilityCountsLazyQuery(
    {
      fetchPolicy: 'network-only', // Always fetch fresh data when selection changes
    },
  )
  const [executeBulkAction, { loading: executing }] = useExecuteBulkActionMutation()
  const [bulkDeleteRows, { loading: deleting }] = useBulkDeleteTableRowsMutation()
  const [bulkDuplicateRows, { loading: duplicating }] = useBulkDuplicateTableRowsMutation()
  const { markRowsProcessing, clearRowsProcessing } = useRowProcessingState(setRowsData)

  // State for delete errors to show in a dialog (from mutation response)
  const [deleteErrors, setDeleteErrors] = useState<BulkDeleteError[] | null>(null)

  useEffect(() => {
    if (selectedCount === 0) {
      return
    }

    const rowIds = isSelectAll ? null : rowSelectionModel.map((id) => String(id))

    getEligibilityCounts({
      variables: {
        input: {
          tableId,
          rowIds,
          filters: isSelectAll ? currentFilters : undefined,
          searchString: isSelectAll ? searchString : undefined,
        },
      },
    })
  }, [
    selectedCount,
    isSelectAll,
    rowSelectionModel,
    tableId,
    currentFilters,
    searchString,
    getEligibilityCounts,
  ])

  // Use previousData while new data is loading to prevent flickering
  const currentData = data ?? previousData
  const buttonCounts = currentData?.bulkActionEligibilityCounts.buttonEligibilityCounts ?? []

  const visibleButtons = buttonCounts.slice(0, MAX_VISIBLE_BUTTONS)
  const hiddenManualTriggerButtons = buttonCounts.slice(MAX_VISIBLE_BUTTONS)

  const getButtonVariant = (button: ButtonEligibilityCount) => {
    return isButtonDestructive(button) ? 'secondaryDestructive' : 'outline'
  }

  const renderButtonLabel = (button: ButtonEligibilityCount) => {
    if (isMultiSelect) {
      const countDisplay = button.hasMore ? `${button.eligibleCount}+` : button.eligibleCount
      return `${button.name} (${countDisplay})`
    }
    return button.name
  }

  const renderButtonIcon = (button: ButtonEligibilityCount): ReactNode => {
    if (button.icon?.name) {
      return <DossIcon name={button.icon.name} size={12} />
    }
    return undefined
  }

  const executeAction = async (button: ButtonEligibilityCount) => {
    if (!button.manualTrigger) {
      snackbar.showError(t('table.bulk_actions.no_manual_trigger'))
      return
    }

    const selectedRowIds = rowSelectionModel.map((id) => String(id))

    const rowIds = isSelectAll ? null : selectedRowIds

    // Mark the rows as processing up front. From here on we only ever clear this state — the
    // pushRowButtonNodes subscription clears executed rows when the action completes, and a fast
    // action's clear event can arrive before the mutation response, so re-asserting the state
    // from the response would re-arm the spinner permanently (INC-1970).
    markRowsProcessing(selectedRowIds, button)

    try {
      const result = await executeBulkAction({
        variables: {
          input: {
            tableId,
            workspaceButtonId: button.id,
            rowIds,
            isSelectAll,
            filters: isSelectAll ? currentFilters : undefined,
            searchString: isSelectAll ? searchString : undefined,
          },
        },
      })

      const { executedCount, skippedCount, errors } = result.data?.executeBulkAction ?? {}

      // Rows the server skipped never had a processing button set in the database, so no clear
      // event will ever arrive for them — clear their spinners here.
      const executedRowIdSet = new Set(result.data?.executeBulkAction.executedRowIds ?? [])
      clearRowsProcessing(selectedRowIds.filter((rowId) => !executedRowIdSet.has(rowId)))

      if (executedCount && executedCount > 0) {
        snackbar.showSuccess(
          executedCount === 1
            ? t('table.bulk_actions.success_action', { count: executedCount })
            : t('table.bulk_actions.success_action_plural', { count: executedCount }),
        )
      }

      if (skippedCount && skippedCount > 0) {
        snackbar.showSuccess(
          skippedCount === 1
            ? t('table.bulk_actions.skipped_singular', { count: skippedCount })
            : t('table.bulk_actions.skipped_plural', { count: skippedCount }),
        )
      }

      if (errors && errors.length > 0) {
        snackbar.showError(
          getBulkDeleteErrorMessage(errors, t) ?? t('table.bulk_actions.error_generic'),
        )
      }

      onClearSelection?.()
    } catch (error) {
      // Nothing was triggered, so no clear event will arrive — remove the spinners.
      clearRowsProcessing(selectedRowIds)
      snackbar.showError(
        error instanceof Error ? error.message : t('table.bulk_actions.error_generic'),
      )
    } finally {
      setConfirmationState({ open: false, type: null, button: null })
    }
  }

  // Handle delete button click - opens confirmation dialog
  const handleDeleteClick = () => {
    setConfirmationState({ open: true, type: 'delete', button: null })
  }

  const handleDelete = async () => {
    // Send the tab id if the current filters match the tab's saved filters. Otherwise, send the
    // current filters. The backend rejects both together.
    const selectAllView =
      tableTabId && !hasUnsavedFilters
        ? { tableTabId, searchString }
        : {
            filters: currentFilters
              ? { filters: currentFilters, filterBooleanOperator: currentFilterBooleanOperator }
              : undefined,
            searchString,
          }
    // If `selectAll` is checked, pass the select all view param. Otherwise, pass the selected
    // rows explicitly.
    const input = isSelectAll
      ? { tableId, selectAllView }
      : { tableId, rowIds: rowSelectionModel.map((id) => String(id)) }

    try {
      const result = await bulkDeleteRows({ variables: { input } })

      const { deletedCount, skippedCount, errors } = result.data?.bulkDeleteTableRows ?? {}

      if (deletedCount && deletedCount > 0) {
        snackbar.showSuccess(
          deletedCount === 1
            ? t('table.bulk_actions.success_delete', { count: deletedCount })
            : t('table.bulk_actions.success_delete_plural', { count: deletedCount }),
        )
      }

      if (skippedCount && skippedCount > 0) {
        snackbar.showSuccess(
          skippedCount === 1
            ? t('table.bulk_actions.skipped_singular', { count: skippedCount })
            : t('table.bulk_actions.skipped_plural', { count: skippedCount }),
        )
      }

      // If there are errors with blocking info, show them in a dialog instead of snackbar
      if (errors && errors.length > 0) {
        const hasBlockingInfo = errors.some(
          (e) => (e.blockingReferences?.length ?? 0) > 0 || (e.blockingRows?.length ?? 0) > 0,
        )
        if (hasBlockingInfo) {
          // Show detailed blocking info in dialog
          setDeleteErrors(errors)
          // Keep the confirmation dialog open to show the errors
          return
        }
        // No blocking info - show generic error in snackbar
        snackbar.showError(
          getBulkDeleteErrorMessage(errors, t) ?? t('table.bulk_actions.error_generic'),
        )
      }

      onClearSelection?.()
      setConfirmationState({ open: false, type: null, button: null })
      setDeleteErrors(null)
    } catch (error) {
      snackbar.showError(
        error instanceof Error ? error.message : t('table.bulk_actions.error_generic'),
      )
      // Deliberately keep the selection: nothing was deleted, so clearing it would just force the
      // user to reselect.
      setConfirmationState({ open: false, type: null, button: null })
      setDeleteErrors(null)
    }
  }

  const handleDuplicate = async () => {
    const rowIds = isSelectAll ? null : rowSelectionModel.map((id) => String(id))
    try {
      const result = await bulkDuplicateRows({
        variables: {
          input: {
            tableId,
            rowIds,
            isSelectAll,
            filters: isSelectAll ? currentFilters : undefined,
            searchString: isSelectAll ? searchString : undefined,
          },
        },
      })

      const { duplicatedCount, errors } = result.data?.bulkDuplicateTableRows ?? {}

      if (duplicatedCount && duplicatedCount > 0) {
        snackbar.showSuccess(
          duplicatedCount === 1
            ? t('table.bulk_actions.success_duplicate', { count: duplicatedCount })
            : t('table.bulk_actions.success_duplicate_plural', { count: duplicatedCount }),
        )
      }

      if (errors && errors.length > 0) {
        snackbar.showError(
          errors.length === 1
            ? t('table.bulk_actions.error_occurred_singular', { count: errors.length })
            : t('table.bulk_actions.error_occurred_plural', { count: errors.length }),
        )
      }

      onClearSelection?.()
    } catch (error) {
      snackbar.showError(
        error instanceof Error ? error.message : t('table.bulk_actions.error_generic'),
      )
    } finally {
      setConfirmationState({ open: false, type: null, button: null })
    }
  }

  const handleButtonClick = (button: ButtonEligibilityCount) => {
    // Prevent execution if over limit
    if (button.hasMore) {
      snackbar.showError(
        t('table.bulk_actions.too_many_rows', {
          max: 1000,
          action: button.name,
        }),
      )
      return
    }

    if (isMultiSelect) {
      setPreviewState({ open: true, button })
    } else if (button.requiresConfirmation) {
      setConfirmationState({ open: true, type: 'button', button })
    } else {
      executeAction(button)
    }
  }

  if (selectedCount === 0) {
    return null
  }

  const hasHiddenButtons = hiddenManualTriggerButtons.length > 0
  const canDelete = permissionsManager.canDeleteRowFromTable(tableId)
  const canDuplicate = permissionsManager.canAddRowToTable(tableId, [])

  // Check if selection count is the only visible button
  const isSelectionCountOnly =
    visibleButtons.length === 0 && !canDuplicate && !canDelete && !hasHiddenButtons

  // Check if visible buttons are the last ones (no duplicate, delete, or More after them)
  const areVisibleButtonsLast = !canDuplicate && !canDelete && !hasHiddenButtons

  // Determine which button is last based on visibility
  const isDeleteLast = canDelete
  const isDuplicateLast = canDuplicate && !canDelete

  const getConfirmationMessage = (): ReactNode => {
    if (confirmationState.type === 'delete') {
      // If we have delete errors with blocking info, show detailed component
      if (deleteErrors && deleteErrors.length > 0) {
        const references = convertErrorsToBlockingReferences(
          deleteErrors,
          t('table.bulk_actions.referenced_rows'),
        )
        return (
          <BlockingReferencesDisplay
            footerMessage={t('table.bulk_actions.remove_references_before_bulk_delete')}
            references={references}
          />
        )
      }

      // Normal confirmation message
      const count = isSelectAll ? totalRowCount : selectedCount
      return count === 1
        ? t('table.bulk_actions.delete_confirmation', { count })
        : t('table.bulk_actions.delete_confirmation_plural', { count })
    }
    if (confirmationState.type === 'duplicate') {
      const count = isSelectAll ? totalRowCount : selectedCount
      return count === 1
        ? t('table.bulk_actions.duplicate_confirmation', { count })
        : t('table.bulk_actions.duplicate_confirmation_plural', { count })
    }
    if (confirmationState.type === 'button' && confirmationState.button) {
      if (confirmationState.button.confirmationMessage) {
        return confirmationState.button.confirmationMessage
      }
      return confirmationState.button.eligibleCount === 1
        ? t('table.bulk_actions.button_confirmation', {
            count: confirmationState.button.eligibleCount,
          })
        : t('table.bulk_actions.button_confirmation_plural', {
            count: confirmationState.button.eligibleCount,
          })
    }
    return ''
  }

  const getConfirmationTitle = (): string => {
    if (confirmationState.type === 'delete') {
      return t('table.bulk_actions.delete_rows_title')
    }
    if (confirmationState.type === 'duplicate') {
      return t('table.bulk_actions.duplicate_rows_title')
    }
    if (confirmationState.type === 'button' && confirmationState.button) {
      return confirmationState.button.name || ''
    }
    return ''
  }

  const getConfirmationVariant = (): 'default' | 'destructive' => {
    if (confirmationState.type === 'delete') {
      return 'destructive'
    }
    if (confirmationState.type === 'button' && confirmationState.button) {
      return isButtonDestructive(confirmationState.button) ? 'destructive' : 'default'
    }
    return 'default'
  }

  const getConfirmLabel = (): string => {
    if (confirmationState.type === 'delete') {
      // If we have delete errors, just show close button
      if (deleteErrors && deleteErrors.length > 0) {
        return t('common.actions.close')
      }
      return t('common.actions.delete')
    }
    if (confirmationState.type === 'duplicate') {
      return t('table.bulk_actions.duplicate')
    }
    return t('common.actions.confirm')
  }

  const getIsLoading = (): boolean => {
    if (confirmationState.type === 'delete') {
      return deleting
    }
    if (confirmationState.type === 'duplicate') {
      return duplicating
    }
    return executing
  }

  const handleConfirm = () => {
    if (confirmationState.type === 'delete') {
      // If we're showing delete errors, just close the dialog
      if (deleteErrors && deleteErrors.length > 0) {
        setConfirmationState({ open: false, type: null, button: null })
        setDeleteErrors(null)
        return
      }
      handleDelete()
    } else if (confirmationState.type === 'duplicate') {
      handleDuplicate()
    } else if (confirmationState.type === 'button' && confirmationState.button) {
      executeAction(confirmationState.button)
    }
  }

  const handleCloseConfirmation = () => {
    setConfirmationState({ open: false, type: null, button: null })
    setDeleteErrors(null)
  }

  return (
    <>
      <div className="flex items-center rounded-md shadow-sm">
        <Button
          className={cn(
            'border-gray-6 border-y border-l w-[110px]',
            isSelectionCountOnly ? 'rounded-md' : 'rounded-l rounded-r-none border-r-0',
          )}
          onClick={() => {
            onClearSelection?.()
          }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          size="xs"
          startIcon={isHovering ? <X className="size-3" /> : undefined}
          variant="outline"
        >
          {isHovering
            ? `${t('signature.clear')}`
            : `${isSelectAll ? totalRowCount : selectedCount} ${t('table.bulk_actions.selected')}`}
        </Button>

        {visibleButtons.map((button, index) => {
          const isLastVisibleButton = index === visibleButtons.length - 1
          const isThisButtonLast = isLastVisibleButton && areVisibleButtonsLast
          const label = renderButtonLabel(button)

          return (
            <Button
              key={button.id}
              className={cn(
                'border-gray-6 border-y border-l max-w-[200px] overflow-hidden',
                isThisButtonLast ? 'rounded-l-none' : 'rounded-none border-r-0',
              )}
              disabled={executing || button.hasMore}
              disabledMessage={t('table.bulk_actions.filter_to_reduce_rows', { max: 1000 })}
              onClick={() => handleButtonClick(button)}
              showTooltipOnOverflow={true}
              size="xs"
              startIcon={renderButtonIcon(button)}
              variant={getButtonVariant(button)}
            >
              {label}
            </Button>
          )
        })}

        {hasHiddenButtons && (
          <MenuDropdown
            align="end"
            items={hiddenManualTriggerButtons.map(
              (button): MenuDropdownItem => ({
                label: renderButtonLabel(button),
                icon: renderButtonIcon(button),
                destructive: isButtonDestructive(button),
                disabled: executing,
                onClick: () => handleButtonClick(button),
              }),
            )}
            sideOffset={12}
            trigger={
              <Button
                className={cn(
                  'border-b-gray-6',
                  isDeleteLast || isDuplicateLast ? 'rounded-l-none' : 'rounded-none border-r-0',
                )}
                disabled={executing}
                size="xs"
                variant="outline"
              >
                {t('table_settings.button_node.more_buttons', {
                  count: hiddenManualTriggerButtons.length,
                })}
                <ChevronDown className="ms-1 size-3" />
              </Button>
            }
          />
        )}

        {canDuplicate && (
          <Button
            className={cn(
              isDuplicateLast ? 'rounded-l-none' : 'rounded-none border-r-0',
              'border-b-gray-6',
            )}
            disabled={duplicating || executing || deleting}
            onClick={() => setConfirmationState({ open: true, type: 'duplicate', button: null })}
            size="xs"
            startIcon={<Copy className="size-3" />}
            variant="outline"
          >
            {t('table.bulk_actions.duplicate')}
          </Button>
        )}

        {canDelete && (
          <Button
            className={cn(
              isDeleteLast ? 'rounded-l-none' : 'rounded-none border-r-0',
              'border-b-gray-6',
            )}
            disabled={deleting || executing || duplicating}
            onClick={handleDeleteClick}
            size="xs"
            variant="outline"
          >
            <Trash2 className="size-3 text-red-10" />
          </Button>
        )}
      </div>

      {confirmationState.open && (
        <ConfirmationDialog
          confirmLabel={getConfirmLabel()}
          description={getConfirmationMessage()}
          isLoading={getIsLoading()}
          isOpen={confirmationState.open}
          onClose={handleCloseConfirmation}
          onConfirm={handleConfirm}
          title={getConfirmationTitle()}
          variant={getConfirmationVariant()}
        />
      )}

      {previewState.button && previewState.open && (
        <BulkActionPreviewDialog
          button={previewState.button}
          currentFilters={currentFilters}
          eligibleRowIds={previewState.button.eligibleRowIds}
          isSelectAll={isSelectAll}
          onClearSelection={() => onClearSelection?.()}
          onClose={() => setPreviewState({ open: false, button: null })}
          searchString={searchString}
          setRowsData={setRowsData}
          table={table}
        />
      )}
    </>
  )
}
